import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenantContext } from "../../../contexts/TenantContext";
import { syncLogsApi } from "../../../services/api/syncLogs";
import Icon from "../../../components/AppIcon";
import PageLayout from "../../../components/navigation/PageLayout";
import Pagination from "../../../components/ui/Pagination";
import ResultsHeader from "../../../components/ui/ResultsHeader";
import FilterPanel, {
  FilterRow,
  FilterField,
} from "../../../components/ui/FilterPanel";

// Standard page size for all list views
const DEFAULT_PAGE_SIZE = 25;

const SyncStatus = {
  InProgress: 0,
  Completed: 1,
  CompletedWithWarnings: 2,
  Failed: 3,
  Cancelled: 4,
};

const getStatusBadge = (status) => {
  switch (status) {
    case SyncStatus.Completed:
      return <span className="badge badge-success">Completed</span>;
    case SyncStatus.CompletedWithWarnings:
      return <span className="badge badge-warning">Warnings</span>;
    case SyncStatus.Failed:
      return <span className="badge badge-error">Failed</span>;
    case SyncStatus.InProgress:
      return <span className="badge badge-info">In Progress</span>;
    case SyncStatus.Cancelled:
      return <span className="badge badge-neutral">Cancelled</span>;
    default:
      return <span className="badge">Unknown</span>;
  }
};

const formatDuration = (ms) => {
  if (!ms && ms !== 0) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.floor(ms / 60000)}m ${((ms % 60000) / 1000).toFixed(2)}s`;
};

const SyncLogsPage = () => {
  const { isTenantAdmin, isGlobalAdmin } = useAuth();
  const {
    selectedTenant,
    needsTenantSelection,
    isGlobalAdmin: isGlobalAdminContext,
  } = useTenantContext();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [syncing, setSyncing] = useState({ hr: false, till: false });
  const [exportLoading, setExportLoading] = useState(false);
  const [syncModal, setSyncModal] = useState(null); // null | { type: 'hr' | 'till' }
  const [syncDateRange, setSyncDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return {
      fromDate: start.toISOString().split("T")[0],
      toDate: end.toISOString().split("T")[0],
    };
  });
  const [syncResult, setSyncResult] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    providerType: "",
    isAutomatic: "",
    status: "",
    days: 30,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  });

  // Only fetch data when tenant is selected (or for non-global admins)
  const shouldFetchData = !isGlobalAdminContext || selectedTenant;

  const fetchLogs = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      if (!shouldFetchData) return;

      setLoading(true);
      try {
        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - filters.days);

        const response = await syncLogsApi.getLogs({
          providerType: filters.providerType || null,
          isAutomatic:
            filters.isAutomatic === "" ? null : filters.isAutomatic === "true",
          status: filters.status === "" ? null : parseInt(filters.status),
          fromDate: fromDate.toISOString(),
          page: page,
          pageSize: pageSize,
        });

        if (response.success) {
          setLogs(response.data.items || []);
          setPagination((prev) => ({
            ...prev,
            page: response.data.page || page,
            pageSize: response.data.pageSize || pageSize,
            totalCount: response.data.totalCount,
            totalPages: response.data.totalPages,
          }));
        }
      } catch (err) {
        setError(err.message || "Failed to load sync logs");
      } finally {
        setLoading(false);
      }
    },
    [filters, shouldFetchData],
  );

  const fetchStats = useCallback(async () => {
    if (!shouldFetchData) return;

    try {
      const response = await syncLogsApi.getStats({ days: filters.days });
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, [filters.days, shouldFetchData]);

  useEffect(() => {
    if (shouldFetchData) {
      fetchLogs(1, pagination.pageSize);
      fetchStats();
    }
  }, [shouldFetchData, selectedTenant?.id]);

  // Refetch when filters change
  useEffect(() => {
    if (shouldFetchData) {
      fetchLogs(1, pagination.pageSize);
      fetchStats();
    }
  }, [filters]);

  const handleTriggerSync = async (type) => {
    setSyncing((prev) => ({ ...prev, [type]: true }));
    setSyncResult(null);
    try {
      let response;
      if (type === "hr") {
        response = await syncLogsApi.triggerHrSync();
      } else {
        response = await syncLogsApi.triggerTillSync(
          syncDateRange.fromDate,
          syncDateRange.toDate,
        );
      }
      setSyncResult({ success: true, data: response?.data, type });
      setTimeout(() => {
        fetchLogs(pagination.page, pagination.pageSize);
        fetchStats();
      }, 1000);
    } catch (err) {
      setSyncResult({
        success: false,
        error: err.message || `Failed to trigger ${type.toUpperCase()} sync`,
      });
      setError(err.message || `Failed to trigger ${type.toUpperCase()} sync`);
    } finally {
      setSyncing((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleOpenSyncModal = (type) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setSyncDateRange({
      fromDate: start.toISOString().split("T")[0],
      toDate: end.toISOString().split("T")[0],
    });
    setSyncResult(null);
    setSyncModal({ type });
  };

  const handleConfirmSync = async () => {
    const type = syncModal?.type;
    if (!type) return;
    await handleTriggerSync(type);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    fetchLogs(newPage, pagination.pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    fetchLogs(1, newPageSize);
  };

  const handleResetFilters = () => {
    setFilters({
      providerType: "",
      isAutomatic: "",
      status: "",
      days: 30,
    });
  };

  // CSV Export helpers
  const exportToCsv = (logsToExport, filename) => {
    const csvContent = [
      [
        "Started At",
        "System",
        "Provider",
        "Sync Type",
        "Type",
        "Status",
        "Records Created",
        "Records Updated",
        "Records Failed",
        "Duration (ms)",
        "Initiated By",
        "Error",
      ],
      ...logsToExport.map((log) => [
        new Date(log.startedAt).toLocaleString(),
        log.providerType,
        log.providerName,
        log.entityType || "All",
        log.isAutomatic ? "Automatic" : "Manual",
        log.status === 1
          ? "Completed"
          : log.status === 2
            ? "Warnings"
            : log.status === 3
              ? "Failed"
              : "In Progress",
        log.recordsCreated,
        log.recordsUpdated,
        log.recordsFailed,
        log.durationMs,
        log.initiatedBy || "",
        log.errorMessage || "",
      ]),
    ]
      .map((row) =>
        row
          .map((cell) => `"${(cell || "").toString().replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCurrentPage = () => {
    if (logs.length === 0) return;
    exportToCsv(logs, `sync-logs-page-${pagination.page}`);
  };

  const handleExportAll = async () => {
    if (pagination.totalCount === 0) return;

    setExportLoading(true);
    try {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - filters.days);

      const response = await syncLogsApi.getLogs({
        providerType: filters.providerType || null,
        isAutomatic:
          filters.isAutomatic === "" ? null : filters.isAutomatic === "true",
        status: filters.status === "" ? null : parseInt(filters.status),
        fromDate: fromDate.toISOString(),
        page: 1,
        pageSize: Math.min(pagination.totalCount, 10000),
      });

      if (response.success && response.data?.items) {
        exportToCsv(
          response.data.items,
          `sync-logs-all-${pagination.totalCount}-records`,
        );
      }
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExportLoading(false);
    }
  };

  const hasActiveFilters =
    filters.providerType ||
    filters.isAutomatic ||
    filters.status ||
    filters.days !== 30;
  const activeFilterCount = [
    filters.providerType,
    filters.isAutomatic,
    filters.status,
    filters.days !== 30,
  ].filter(Boolean).length;

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Settings", path: "/settings" },
    { label: "Sync", path: "/settings/sync-logs" },
  ];

  if (!isTenantAdmin && !isGlobalAdmin) {
    return (
      <PageLayout
        title="Access Denied"
        description="You don't have permission to view this page."
        breadcrumbs={breadcrumbs}
      >
        <div className="card p-8 text-center">
          <Icon
            name="ShieldAlert"
            size={48}
            className="mx-auto mb-4 text-warning"
          />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            Only administrators can access sync logs.
          </p>
        </div>
      </PageLayout>
    );
  }

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdminContext && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Sync - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Sync"
          description="Monitor and run data synchronization."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the status
              bar to view their sync data.
            </p>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sync - {selectedTenant?.name || "Rocket Tronc"}</title>
      </Helmet>

      <PageLayout
        title="Sync"
        description={
          selectedTenant
            ? `${selectedTenant.name} - Monitor and run data synchronization.`
            : "Monitor and run data synchronization."
        }
        breadcrumbs={breadcrumbs}
      >
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-6 ">
          <button
            className="btn dbtn-outline w-full sm:w-auto"
            onClick={() => handleOpenSyncModal("hr")}
            disabled={syncing.hr}
          >
            {syncing.hr ? (
              <div className="spinner w-4 h-4" />
            ) : (
              <Icon name="Users" size={18} />
            )}
            Sync HR
          </button>
          <button
            className="btn btn-outline w-full sm:w-auto"
            onClick={() => handleOpenSyncModal("till")}
            disabled={syncing.till}
          >
            {syncing.till ? (
              <div className="spinner w-4 h-4" />
            ) : (
              <Icon name="PoundSterling" size={18} />
            )}
            Sync TRONC / Clock Records
          </button>
        </div>

        {/* Sync Modal */}
        {syncModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-lg w-full">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon
                      name={syncModal.type === "hr" ? "Users" : "PoundSterling"}
                      size={20}
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">
                      {syncModal.type === "hr"
                        ? "Sync HR Data"
                        : "Sync TRONC & Clock Records"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {syncModal.type === "hr"
                        ? "Syncs locations, job roles, and employees from the HR provider."
                        : "Select a date range for TRONC records and clock records. Existing records within the range will be updated."}
                    </p>
                  </div>
                </div>
                <button
                  className="p-2 hover:bg-muted rounded-lg"
                  onClick={() => setSyncModal(null)}
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {syncModal.type === "till" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={syncDateRange.fromDate}
                        onChange={(e) =>
                          setSyncDateRange((prev) => ({
                            ...prev,
                            fromDate: e.target.value,
                          }))
                        }
                        max={syncDateRange.toDate}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={syncDateRange.toDate}
                        onChange={(e) =>
                          setSyncDateRange((prev) => ({
                            ...prev,
                            toDate: e.target.value,
                          }))
                        }
                        min={syncDateRange.fromDate}
                        max={new Date().toISOString().split("T")[0]}
                        className="input w-full"
                      />
                    </div>
                  </div>
                )}

                {syncModal.type === "till" && (
                  <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="Info" size={14} className="text-info" />
                      <span className="font-medium text-foreground">
                        How sync works
                      </span>
                    </div>
                    <p>
                      Records within the selected date range will be fetched
                      from the connected provider. New records are created and
                      existing records are updated with the latest data.
                    </p>
                  </div>
                )}

                {syncResult && (
                  <div
                    className={`rounded-lg p-3 text-sm ${syncResult.success ? "bg-success/10 text-success" : "bg-error/10 text-error"}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        name={
                          syncResult.success ? "CheckCircle" : "AlertCircle"
                        }
                        size={16}
                      />
                      <span className="font-medium">
                        {syncResult.success ? "Sync completed" : "Sync failed"}
                      </span>
                    </div>
                    {syncResult.error && (
                      <p className="mt-1">{syncResult.error}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-border flex justify-end gap-3">
                <button
                  className="btn btn-outline"
                  onClick={() => setSyncModal(null)}
                >
                  {syncResult ? "Close" : "Cancel"}
                </button>
                {!syncResult && (
                  <button
                    className="btn btn-primary"
                    onClick={handleConfirmSync}
                    disabled={syncing.hr || syncing.till}
                  >
                    {syncing.hr || syncing.till ? (
                      <>
                        <div className="spinner w-4 h-4" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Icon name="RefreshCw" size={16} />
                        Run Sync
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setError(null)}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="card p-4">
              <p className="text-sm text-muted-foreground">Total Syncs</p>
              <p className="text-2xl font-bold font-data">{stats.totalSyncs}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-muted-foreground">Successful</p>
              <p className="text-2xl font-bold font-data text-success">
                {stats.successfulSyncs}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold font-data text-error">
                {stats.failedSyncs}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-muted-foreground">Automatic</p>
              <p className="text-2xl font-bold font-data">
                {stats.automaticSyncs}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-muted-foreground">Manual</p>
              <p className="text-2xl font-bold font-data">
                {stats.manualSyncs}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-muted-foreground">Avg Duration</p>
              <p className="text-2xl font-bold font-data">
                {formatDuration(stats.averageDurationMs)}
              </p>
            </div>
          </div>
        )}

        {/* Filters - Standardized Layout */}
        <FilterPanel
          title="Filters"
          onReset={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          activeFilterCount={activeFilterCount}
          className="mb-6"
        >
          <FilterRow>
            <FilterField label="System">
              <select
                className="input w-full"
                value={filters.providerType}
                onChange={(e) =>
                  handleFilterChange("providerType", e.target.value)
                }
              >
                <option value="">All Systems</option>
                <option value="HR">HR System</option>
                <option value="Till">Till System</option>
              </select>
            </FilterField>

            <FilterField label="Type">
              <select
                className="input w-full"
                value={filters.isAutomatic}
                onChange={(e) =>
                  handleFilterChange("isAutomatic", e.target.value)
                }
              >
                <option value="">All Types</option>
                <option value="true">Automatic</option>
                <option value="false">Manual</option>
              </select>
            </FilterField>

            <FilterField label="Status">
              <select
                className="input w-full"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="1">Completed</option>
                <option value="2">With Warnings</option>
                <option value="3">Failed</option>
                <option value="0">In Progress</option>
              </select>
            </FilterField>

            <FilterField label="Time Period">
              <select
                className="input w-full"
                value={filters.days}
                onChange={(e) =>
                  handleFilterChange("days", parseInt(e.target.value))
                }
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </FilterField>
          </FilterRow>
        </FilterPanel>

        {/* Results Header with Export */}
        <ResultsHeader
          totalCount={pagination.totalCount}
          entityName="sync logs"
          onExportCurrentPage={handleExportCurrentPage}
          onExportAll={handleExportAll}
          exportLoading={exportLoading}
          exportDisabled={logs.length === 0}
        >
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => fetchLogs(pagination.page, pagination.pageSize)}
          >
            <Icon name="RefreshCw" size={16} />
            Refresh
          </button>
        </ResultsHeader>

        {/* Logs Table */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>System</th>
                  <th>Provider</th>
                  <th>Sync Type</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Records</th>
                  <th>Duration</th>
                  <th>Initiated By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No sync logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-muted/50">
                      <td className="font-data text-sm">
                        {new Date(log.startedAt).toLocaleString()}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Icon
                            name={
                              log.providerType === "HR"
                                ? "Users"
                                : "PoundSterling"
                            }
                            size={16}
                            className="text-muted-foreground"
                          />
                          {log.providerType}
                        </div>
                      </td>
                      <td>{log.providerName}</td>
                      <td>
                        <span
                          className={`badge ${
                            log.entityType === "Location"
                              ? "badge-info"
                              : log.entityType === "JobRole"
                                ? "badge-secondary"
                                : log.entityType === "Employee"
                                  ? "badge-primary"
                                  : log.entityType === "ClockRecord"
                                    ? "badge-warning"
                                    : log.entityType === "TroncRecord"
                                      ? "badge-success"
                                      : "badge-neutral"
                          }`}
                        >
                          {log.entityType === "JobRole"
                            ? "Job Role"
                            : log.entityType === "ClockRecord"
                              ? "Clock Record"
                              : log.entityType === "TroncRecord"
                                ? "TRONC Record"
                                : log.entityType || "All"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${log.isAutomatic ? "badge-secondary" : "badge-outline"}`}
                        >
                          {log.isAutomatic ? "Auto" : "Manual"}
                        </span>
                      </td>
                      <td>{getStatusBadge(log.status)}</td>
                      <td className="font-data">
                        <span className="text-success">
                          +{log.recordsCreated}
                        </span>
                        {" / "}
                        <span className="text-primary">
                          ~{log.recordsUpdated}
                        </span>
                        {log.recordsFailed > 0 && (
                          <>
                            {" / "}
                            <span className="text-error">
                              ?{log.recordsFailed}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="font-data">
                        {formatDuration(log.durationMs)}
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {log.initiatedBy}
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Icon name="Eye" size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Standardized Pagination */}
          {pagination.totalPages > 0 && (
            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalCount={pagination.totalCount}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>

        {/* Log Detail Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <h2 className="text-xl font-semibold">Sync Log Details</h2>
                <button
                  className="p-2 hover:bg-muted rounded-lg"
                  onClick={() => setSelectedLog(null)}
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">System</p>
                    <p className="font-medium">{selectedLog.providerType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Provider</p>
                    <p className="font-medium">{selectedLog.providerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sync Type</p>
                    <p className="font-medium">
                      {selectedLog.entityType === "JobRole"
                        ? "Job Role"
                        : selectedLog.entityType === "ClockRecord"
                          ? "Clock Record"
                          : selectedLog.entityType === "TroncRecord"
                            ? "TRONC Record"
                            : selectedLog.entityType || "All"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium">
                      {selectedLog.isAutomatic ? "Automatic" : "Manual"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    {getStatusBadge(selectedLog.status)}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Started</p>
                    <p className="font-data">
                      {new Date(selectedLog.startedAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="font-data">
                      {selectedLog.completedAt
                        ? new Date(selectedLog.completedAt).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Duration</p>
                    <p className="font-data">
                      {formatDuration(selectedLog.durationMs)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Initiated By
                    </p>
                    <p className="font-medium">
                      {selectedLog.initiatedBy || "-"}
                    </p>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h3 className="font-semibold mb-2">Record Statistics</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-2xl font-bold font-data">
                        {selectedLog.totalRecordsFetched}
                      </p>
                      <p className="text-xs text-muted-foreground">Fetched</p>
                    </div>
                    <div className="text-center p-3 bg-success/10 rounded-lg">
                      <p className="text-2xl font-bold font-data text-success">
                        {selectedLog.recordsCreated}
                      </p>
                      <p className="text-xs text-muted-foreground">Created</p>
                    </div>
                    <div className="text-center p-3 bg-primary/10 rounded-lg">
                      <p className="text-2xl font-bold font-data text-primary">
                        {selectedLog.recordsUpdated}
                      </p>
                      <p className="text-xs text-muted-foreground">Updated</p>
                    </div>
                    <div className="text-center p-3 bg-warning/10 rounded-lg">
                      <p className="text-2xl font-bold font-data text-warning">
                        {selectedLog.recordsSkipped}
                      </p>
                      <p className="text-xs text-muted-foreground">Skipped</p>
                    </div>
                  </div>
                </div>

                {selectedLog.errorMessage && (
                  <div className="border-t border-border pt-4">
                    <h3 className="font-semibold mb-2 text-error">Error</h3>
                    <pre className="bg-error/10 text-error p-3 rounded-lg text-sm overflow-x-auto">
                      {selectedLog.errorMessage}
                    </pre>
                  </div>
                )}

                {selectedLog.warnings && (
                  <div className="border-t border-border pt-4">
                    <h3 className="font-semibold mb-2 text-warning">
                      Warnings
                    </h3>
                    <pre className="bg-warning/10 text-warning p-3 rounded-lg text-sm overflow-x-auto">
                      {selectedLog.warnings}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default SyncLogsPage;
